import { useEffect, useRef, useState } from "react";
import { getMediaBlob } from "../services/db/mediaRepo";
import { cancelRecording, retryRecording } from "../services/db/recordingRepo";
import type { TranscriptionQueueJob } from "../services/db/schema";
import { notifyTranscriptionQueue } from "../services/sync/transcriptionQueue";

export function RecordingList({ recordings, onChanged, disabled }: {
  recordings: TranscriptionQueueJob[]; onChanged: () => void; disabled: boolean;
}): JSX.Element | null {
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);
  if (!recordings.length) return null;
  async function act(job: TranscriptionQueueJob, remove: boolean): Promise<void> {
    if (remove && !window.confirm("Remove this recording? Other recordings and the photo will stay saved.")) return;
    setPending(true);
    setError(undefined);
    try {
      if (remove) await cancelRecording(job.jobId);
      else await retryRecording(job.jobId);
      onChanged();
      notifyTranscriptionQueue();
    } catch { setError("Could not update this recording. Please try again."); }
    finally { setPending(false); }
  }
  return <div className="recording-list" data-swipe-ignore="true">
    <h2>{recordings.length} {recordings.length === 1 ? "recording" : "recordings"} for this photo</h2>
    {recordings.map((job, index) => <details key={job.jobId} className="recording-list__item">
      <summary>Recording {index + 1} · {statusLabel(job)}</summary>
      <RecordingAudio blobKey={job.audioBlobKey} />
      {job.transcript && <p className="candidate-note">{job.transcript}</p>}
      {job.lastError && <p className="helper">{job.lastError}</p>}
      <div className="button-row">
        {(job.status === "error" || job.status === "retry_wait") && <button className="button" type="button" disabled={disabled || pending} onClick={() => void act(job, false)}>Retry transcription</button>}
        {!job.reviewedAt && <button className="button" type="button" disabled={disabled || pending} onClick={() => void act(job, true)}>Remove this recording</button>}
      </div>
    </details>)}
    {error && <p role="alert" className="helper helper--warning">{error}</p>}
  </div>;
}

function RecordingAudio({ blobKey }: { blobKey: string }): JSX.Element {
  const [url, setUrl] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  async function load(): Promise<void> {
    setLoading(true);
    try {
      const media = await getMediaBlob(blobKey);
      if (!mounted.current) return;
      if (media) setUrl(URL.createObjectURL(media.blob));
      else setUnavailable(true);
    } catch { setUnavailable(true); }
    finally { setLoading(false); }
  }
  return url ? <audio controls src={url} preload="metadata" /> : unavailable ? <p className="helper">Audio is no longer stored here. Saved transcripts are shown below.</p> :
    <button className="button" type="button" disabled={loading} onClick={() => void load()}>{loading ? "Loading recording..." : "Listen to recording"}</button>;
}

function statusLabel(job: TranscriptionQueueJob): string {
  if (job.status === "completed") return job.reviewedAt ? "Reviewed" : "Transcribed";
  if (job.status === "uploading") return "Uploading";
  if (job.status === "transcribing") return "Transcribing";
  if (job.status === "error") return "Needs attention";
  return "Saved on this device";
}
